import { reverse } from 'named-urls'
import React from 'react'
import { Link, useLocation } from 'react-router-dom'

import { SECTION_NAMES } from '@constants/sectionNames'
import { EmptyState, List, Table, useMediaQueryContext } from '@fourthwall/components'
import type { FromLocationState } from '@types'
import { routing } from '@utils/routing'

import type { AnalyticsReportsItem } from '../../AnalyticsReports.types'
import { AnalyticsReportsListItem } from '../AnalyticsReportsListItem/AnalyticsReportsListItem'
import { ReportExportButton } from '../ReportExportButton/ReportExportButton'
import { ReportName } from '../ReportName/ReportName'
import * as S from './AnalyticsReportsData.styled'

export type AnalyticsReportsDataProps = {
  data: AnalyticsReportsItem[]
  isLoading?: boolean
  precision: string
  onExport: (reportId: string) => Promise<void>
}

const getRowLink = (report: any) => ({
  pathname: reverse(routing.analyticsV2.reports.report.self, {
    reportId: report.id,
  }),
  search: location.search,
})

export const AnalyticsReportsData = ({
  data,
  isLoading,
  precision,
  onExport,
}: AnalyticsReportsDataProps) => {
  const media = useMediaQueryContext()
  const location = useLocation()
  const locationState = {
    from: { label: SECTION_NAMES.analyticsReports, location },
  } satisfies FromLocationState

  const emptyComponent = (
    <EmptyState
      title="There are no reports available"
      variant={media.tablet ? 'primary' : 'secondary'}
    />
  )

  if (!isLoading && !data.length)
    return <S.EmptyStateContainer>{emptyComponent}</S.EmptyStateContainer>

  if (!media.tablet) {
    return (
      <List
        data={data}
        isLoading={isLoading}
        emptyComponent={emptyComponent}
        renderItem={(item: AnalyticsReportsItem) => (
          <AnalyticsReportsListItem
            item={item}
            precision={precision}
            onExport={() => onExport(item.id)}
          />
        )}
        locationState={locationState}
        getItemLink={getRowLink}
      />
    )
  }

  // TODO: Handle location state in table
  return (
    <Table
      headers={[
        {
          id: 'name',
          label: 'Report',
          render: ({ name, decorator, externalSource }) => (
            <ReportName name={name} decorator={decorator} externalSource={externalSource} />
          ),
        },
        {
          id: 'download',
          label: 'CSV download',
          render: ({ name, id, supportsSelectedPrecision }) => (
            <ReportExportButton
              disabled={!supportsSelectedPrecision}
              tooltipMessage={`${name} report cannot be downloaded, as this report does not support grouping by ${precision}`}
              onExport={() => onExport(id)}
            />
          ),
          alignment: 'right' as const,
        },
      ]}
      data={data}
      emptyComponent={emptyComponent}
      isLoading={isLoading}
      rowComponent={Link}
      getRowLink={getRowLink}
    />
  )
}
